import { useState } from 'react'

import { pageCountOf, pageSlice } from '../shell/stripEventsModel.tsx'

/**
 * 목록 표의 쪽 나눔(eric 2026-09-30 「작업이나 작업 이력 리스트에서.. 리스트로 만들어줘」).
 *
 * 작업 · 이력 두 표가 처음 쓴다. 시료 · 레시피 · 랩웨어 · 알람 · 로그도 같은 틀로 붙이면 된다 —
 * 컨테이너가 `useListPage` 로 쪽을 쥐고 `listPageQuery` 로 서버에 묻고, 화면은 `ListPaging` 을 받아
 * 표 아래 `Pagination` 을 그린다.
 *
 * 🔴 **쪽마다 서버에서 받는다** — `limit`·`offset`. 전부 받아 자르지 않는다(데모 목록만 자른다).
 * 🔴 **쪽 수는 `total_count` 가 정한다**(`Page.total_count` · 필터 적용 후 전체 건수). 서버가 안 주면
 *    「받은 만큼 찼는가」로 다음 쪽이 있는지만 안다 — 총량을 지어내지 않는다.
 * 🔴 **정렬은 서버가 끝낸다**(`app/services/batches.py` `list_page` 「부르는 쪽이 다시 정렬하지 않는다」).
 *    쪽 안에서 다시 줄 세우면 쪽 사이 순서가 깨진다.
 */

/** 화면이 받는 쪽 정보. 🔴 **안 넘기면 쪽 넘김이 없다**(태블릿 · 시험 · 데모 기본값) */
export interface ListPaging {
  /** 1부터 */
  page: number
  pageCount: number
  /** 필터 적용 후 전체 건수(`total_count`). 모르면 `null` — 그땐 「N건」을 받은 행 수로 센다 */
  totalCount: number | null
  onChange: (page: number) => void
}

/** 1부터 센 쪽 → 서버 `limit`·`offset` */
export function listPageQuery(page: number, size: number): { limit: number; offset: number } {
  return { limit: size, offset: (Math.max(1, page) - 1) * size }
}

/** 받은 목록에서 한 쪽(1부터) — 서버를 안 때리는 데모 갈래만 쓴다 */
export function listPageSlice<T>(rows: readonly T[], page: number, size: number): T[] {
  return pageSlice(rows, Math.max(1, page) - 1, size)
}

/**
 * 쪽 수. 총량이 있으면 그것으로, 없으면 「이 쪽이 찼으면 다음 쪽이 하나 더 있다」.
 * 0건이어도 1쪽이다.
 */
export function listPageCount(
  total: number | null,
  page: number,
  received: number,
  size: number,
): number {
  if (total !== null) return pageCountOf(total, size)
  return Math.max(1, page) + (received >= size ? 1 : 0)
}

/**
 * 컨테이너가 쥐는 쪽 상태.
 *
 * - 쪽을 넘기는 동안(새 쪽 받는 중) 쪽 넘김이 사라졌다 서지 않게 **마지막으로 안 쪽 수**를 쥔다
 * - 🔴 **총량이 줄어 지금 쪽이 끝 밖이 되면 끝 쪽으로 붙인다.** 진행 중 작업이 끝나 이력으로 넘어가면
 *   작업 목록의 총량이 준다 — 그대로 두면 빈 쪽을 받아 「작업 없음」을 그린다(있는데 없다고 말한다)
 */
export function useListPage(): {
  page: number
  setPage: (page: number) => void
  /** 받은 결과로 쪽 수를 확정한다. 받는 중이면 `pending` 을 참으로 */
  settle: (pages: number, pending: boolean) => number
} {
  const [page, setPageState] = useState(1)
  const [knownPages, setKnownPages] = useState(1)
  const setPage = (next: number) => setPageState(Math.max(1, Math.floor(next)))
  const settle = (pages: number, pending: boolean): number => {
    if (pending) return Math.max(knownPages, page)
    if (knownPages !== pages) setKnownPages(pages)
    if (page > pages) setPageState(pages)
    return pages
  }
  return { page, setPage, settle }
}
